import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Button,
  InputCheckbox,
  IconButton,
  HeaderText,
  BodyText,
  CloseIcon,
} from 'shared-portal-component';

import SearchInput from '@/components/molecules/SearchInput/SearchInput';
import {
  selectExternalUsers,
  selectDraft,
  selectInternalUsers,
  selectEditingSixId,
} from '@/store/selectors/createSix';
import {
  resetCreateSixState,
  setDraftField,
  setStep,
} from '@/store/slices/createSix/createSixSlice';
import { useAppDispatch } from '@/store';
import TwoSlot from '@/components/molecules/TwoSlot/TwoSlot';
import '@/components/organism/CreateSix/steps/style.scss';
import { createSixThunk } from '@/store/thunks/create-six/createSixThunk';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { fetchSixCatalog } from '@/store/thunks/sixCatalog/fetchSixCatalog';
import { updateSixThunk } from '@/store/thunks/create-six/updateSixThunk';
import type { SixOwner } from '@/types/six/Six';

const StepExternalParticipants: React.FC = () => {
  const dispatch = useAppDispatch();
  const users = useSelector(selectExternalUsers);
  const draft = useSelector(selectDraft);
  const internalUsers = useSelector(selectInternalUsers);
  // Initial state from draft, so that selection persists on revisit
  const [selectedIds, setSelectedIds] = useState<string[]>(draft.externalParticipantIds);
  const editingSixId = useSelector(selectEditingSixId);

  // Handle checkbox select/deselect
  const handleCheckboxChange = (userId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const newIds = checked ? [...prev, userId] : prev.filter((id) => id !== userId);
      dispatch(setDraftField({ key: 'externalParticipantIds', value: newIds }));

      return newIds;
    });
  };

  // Handle remove from selected chips
  const handleRemoveSelected = (userId: string) => {
    setSelectedIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleInvite = () => {
    dispatch(setDraftField({ key: 'externalParticipantIds', value: selectedIds }));
    dispatch(setStep('NewExternalParticipant'));
  };

  const handleProceed = async() => {
    const owners: SixOwner[] = draft.ownerIds
      .map((id) => {
        const user = internalUsers.find((user) => user.id === id);
        if (user === undefined) return null;
        return {
          id: user.id,
          name: user.name,
        };
      })
      .filter((owner): owner is SixOwner => owner !== null);

    const payload = {
      ...draft,
      ownerIds: owners,
    };

    if (editingSixId !== null) {
      await dispatch(
        updateSixThunk({
          id: editingSixId,
          ...payload,
        })
      ).unwrap();
      dispatch(hideModal());
      dispatch(resetCreateSixState());
      void dispatch(fetchSixCatalog());
    } else {
      // createSixThunk handles polling for bootstrapNodes internally
      await dispatch(createSixThunk(payload)).unwrap();
      dispatch(hideModal());
      dispatch(resetCreateSixState());
    }
  };

  const handleBack = () => dispatch(setStep('InternalParticipants'));

  const MIN = 0;

  return (
    <Container fluid>
      <Row spacing="md" justify="start">
        <Col md={6}>
          <SearchInput placeholder="Search for a user" />
        </Col>
      </Row>
      <TwoSlot>
        {/* LEFT: External user list with checkboxes */}
        <TwoSlot.FirstSlot>
          <Container fluid>
            <Row spacing="none" justify="start" align="center">
              <Col size={1}> </Col>
              <Col size={5}>
                <HeaderText className="header-text" text="Name" as="h1" size="h1" />
              </Col>
              <Col size={5}>
                <HeaderText className="header-text" text="Email" as="h1" size="h1" />
              </Col>
            </Row>
          </Container>
          <Container fluid className="user-list-scroll">
            {users.map((user) => (
              <Row key={user.id} spacing="none" align="center">
                <Col size={6}>
                  <div>
                    <InputCheckbox
                      checked={selectedIds.includes(user.id)}
                      label={
                        <div>
                          <BodyText className="body-text" as="p">
                            {user.name}
                          </BodyText>
                        </div>
                      }
                      onChange={(checked: boolean) => {
                        handleCheckboxChange(user.id, checked);
                      }}
                      id={`external-checkbox-${user.id}`}
                    />
                  </div>
                </Col>
                <Col size={6}>
                  {
                    <BodyText className="body-text" as="p">
                      {user.email}
                    </BodyText>
                  }
                </Col>
              </Row>
            ))}
          </Container>
          {/* Optionally: add Invite New button */}
          <Row spacing="md" justify="center" className="mt-5">
            <Button
              text="Invite New"
              variant="primary"
              size="md"
              className="mt-5"
              onClick={handleInvite}
            />
          </Row>
        </TwoSlot.FirstSlot>

        {/* RIGHT: Selected chips */}
        <TwoSlot.SecondSlot>
          <Container fluid>
            <div className="px-4">
              <HeaderText
                className="header-text"
                text="Selected external participants"
                as="h1"
                size="h1"
              />
              {selectedIds.length === MIN ? (
                <p>No participants selected</p>
              ) : (
                selectedIds.map((id) => {
                  const user = users.find((user) => user.id === id);
                  if (user == null) return null;
                  return (
                    <Row spacing="none" key={id} align="center">
                      <IconButton
                        icon={<CloseIcon size="1x" />}
                        size="md"
                        ariaLabel="Remove"
                        variant="secondary"
                        onClick={() => {
                          handleRemoveSelected(id);
                        }}
                      />
                      <BodyText className="body-text" as="p">
                        {user.name} <span>{user.email}</span>
                      </BodyText>
                    </Row>
                  );
                })
              )}
            </div>
          </Container>
        </TwoSlot.SecondSlot>
      </TwoSlot>
      <Row spacing="md" justify="center">
        <Button text="Go Back" variant="primary" size="md" onClick={handleBack} />
        <Button
          text="Proceed"
          variant="primary"
          size="md"
          onClick={() => {
            void handleProceed();
          }}
        />
      </Row>
    </Container>
  );
};

export default StepExternalParticipants;
